import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Kanban, Pencil, Plus, Star, Trash2 } from 'lucide-react';

import type { BoardConfig } from '@tasks/contracts';

import { createBoard, deleteBoard, setDefaultBoard, updateBoard } from '../../api/boards';
import { listComponents } from '../../api/components';
import { listWorkflows } from '../../api/workflow';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Combobox } from '../../components/Combobox';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { IconButton } from '../../components/IconButton';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { useProjectBoards } from '../../hooks/useBoards';
import { humanizeError } from '../../lib/errors';
import styles from './SettingsPages.module.css';

/**
 * Доски проекта (UC-PRJ-02, docs/06 §3.5).
 *
 * Доска — представление задач проекта, поэтому создание и удаление досок
 * не влияет на сами задачи. У каждой доски — свой набор отображаемых
 * компонентов (docs/13 §7): пустой набор означает «все компоненты проекта»,
 * и это состояние по умолчанию. Управление доступно администратору проекта:
 * страница живёт внутри ProjectLayout, который уже проверил права.
 */
export function BoardsSettingsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { boards, isLoading } = useProjectBoards(key);

  const componentsQuery = useQuery({
    queryKey: queryKeys.projects.components(key),
    queryFn: ({ signal }) => listComponents(key, { signal }),
    enabled: key.length > 0,
  });
  const components = componentsQuery.data ?? [];

  // Workflow выбирается при создании доски: её колонки — статусы этого набора
  // (docs/03 §1). Смена набора у существующей доски не предусмотрена: задачи
  // доски остались бы в статусах чужого флоу.
  const workflowsQuery = useQuery({
    queryKey: queryKeys.projects.workflows(key),
    queryFn: ({ signal }) => listWorkflows(key, signal),
    enabled: key.length > 0,
  });
  const workflows = workflowsQuery.data ?? [];

  const [isCreateOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newComponentIds, setNewComponentIds] = useState<readonly string[]>([]);
  const [newWorkflowId, setNewWorkflowId] = useState<string | null>(null);
  const [boardToRename, setBoardToRename] = useState<BoardConfig | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [boardToScope, setBoardToScope] = useState<BoardConfig | null>(null);
  const [scopeComponentIds, setScopeComponentIds] = useState<readonly string[]>([]);
  const [boardToDelete, setBoardToDelete] = useState<BoardConfig | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.boards(key) });
    // Набор компонентов влияет на выдачу доски и редирект со старого `/board`.
    void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(key) });
  };

  const reportError = (cause: unknown): void => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const componentItems = components.map((component) => ({
    id: component.id,
    label: component.name,
  }));

  const createMutation = useMutation({
    mutationFn: () =>
      createBoard(key, {
        name: newName.trim(),
        ...(newComponentIds.length > 0 ? { componentIds: [...newComponentIds] } : {}),
        ...(newWorkflowId === null ? {} : { workflowId: newWorkflowId }),
      }),
    onSuccess: (board) => {
      setCreateOpen(false);
      setNewName('');
      setNewComponentIds([]);
      setNewWorkflowId(null);
      invalidate();
      toast.success(`Доска «${board.name}» создана`);
    },
    onError: reportError,
  });

  const renameMutation = useMutation({
    mutationFn: (board: BoardConfig) => updateBoard(key, board.id, { name: renameValue.trim() }),
    onSuccess: () => {
      setBoardToRename(null);
      setRenameValue('');
      invalidate();
      toast.success('Доска переименована');
    },
    onError: reportError,
  });

  const scopeMutation = useMutation({
    mutationFn: (board: BoardConfig) =>
      updateBoard(key, board.id, { componentIds: [...scopeComponentIds] }),
    onSuccess: (board) => {
      setBoardToScope(null);
      invalidate();
      toast.success(
        board.componentIds.length === 0
          ? `«${board.name}» показывает все компоненты`
          : `«${board.name}»: компонентов — ${board.componentIds.length}`,
      );
    },
    onError: reportError,
  });

  const defaultMutation = useMutation({
    mutationFn: (board: BoardConfig) => setDefaultBoard(key, board.id),
    onSuccess: (board) => {
      invalidate();
      toast.success(`«${board.name}» — доска по умолчанию`);
    },
    onError: reportError,
  });

  const deleteMutation = useMutation({
    mutationFn: (board: BoardConfig) => deleteBoard(key, board.id),
    onSuccess: () => {
      setBoardToDelete(null);
      invalidate();
      toast.success('Доска удалена');
    },
    onError: reportError,
  });

  if (isLoading) {
    return <Skeleton variant="row" count={3} />;
  }

  return (
    <div className={styles['section']}>
      <div className={styles['sectionHeader']}>
        <h2>Доски</h2>
        <Button
          variant="primary"
          icon={<Plus size={15} />}
          onClick={() => {
            setNewName('');
            setNewComponentIds([]);
            setNewWorkflowId(
              workflows.find((workflow) => workflow.isDefault)?.id ?? workflows[0]?.id ?? null,
            );
            setCreateOpen(true);
          }}
        >
          Создать доску
        </Button>
      </div>

      <p className={styles['hint']}>
        Доска — представление задач проекта: колонки берутся из workflow, а задачи
        остаются общими для всех досок. Доска по умолчанию открывается по старой
        ссылке <code>/board</code> и хоткеем <kbd>B</kbd>.
      </p>

      {boards.length === 0 ? (
        <EmptyState
          icon={<Kanban size={20} />}
          title="Досок ещё нет"
          description="Создайте первую доску — она появится в меню и откроется по умолчанию."
        />
      ) : (
        <ul className={styles['boardList']}>
          {boards.map((board) => (
            <li key={board.id} className={styles['boardRow']}>
              <span aria-hidden className={styles['boardIcon']}>
                <Kanban size={16} />
              </span>
              <span className={styles['boardName']}>{board.name}</span>
              {board.isDefault ? <span className={styles['boardDefault']}>по умолчанию</span> : null}
              {/* Пустой набор — «все компоненты»: подпись снимает вопрос
                  «куда делись задачи» при переходе между досками. */}
              <span className={styles['boardScope']}>
                {board.componentIds.length === 0
                  ? 'все компоненты'
                  : `компонентов: ${board.componentIds.length}`}
              </span>
              <span className={styles['boardScope']}>
                {workflows.find((item) => item.id === board.workflowId)?.name ?? 'workflow'}
              </span>

              <span className={styles['spacer']} />

              {board.isDefault ? null : (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Star size={14} />}
                  onClick={() => defaultMutation.mutate(board)}
                  disabled={defaultMutation.isPending}
                >
                  Сделать основной
                </Button>
              )}
              <IconButton
                size="sm"
                label={`Компоненты доски «${board.name}»`}
                icon={<Boxes size={15} />}
                onClick={() => {
                  setBoardToScope(board);
                  setScopeComponentIds(board.componentIds);
                }}
              />
              <IconButton
                size="sm"
                label={`Переименовать доску «${board.name}»`}
                icon={<Pencil size={15} />}
                onClick={() => {
                  setBoardToRename(board);
                  setRenameValue(board.name);
                }}
              />
              <IconButton
                size="sm"
                label={`Удалить доску «${board.name}»`}
                icon={<Trash2 size={15} />}
                onClick={() => setBoardToDelete(board)}
              />
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        title="Новая доска"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={createMutation.isPending}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={createMutation.isPending}
              disabled={newName.trim().length === 0}
              onClick={() => createMutation.mutate()}
            >
              Создать
            </Button>
          </>
        }
      >
        <Input
          label="Название доски"
          maxLength={80}
          value={newName}
          placeholder="Например, Доска релиза"
          hint="Название уникально внутри проекта"
          onChange={(event) => setNewName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && newName.trim().length > 0) {
              createMutation.mutate();
            }
          }}
        />
        {components.length > 0 ? (
          <Combobox
            label="Компоненты"
            multiple
            items={componentItems}
            value={newComponentIds}
            isLoading={componentsQuery.isPending}
            ariaLabel="Компоненты новой доски"
            emptyText="В проекте пока нет компонентов"
            onChange={(value) => setNewComponentIds(value)}
          />
        ) : null}
        {workflows.length > 1 ? (
          <label className={styles['workflowPickRow']}>
            <span>Workflow доски</span>
            <select
              className={styles['workflowPick']}
              value={newWorkflowId ?? ''}
              onChange={(event) => setNewWorkflowId(event.target.value || null)}
            >
              {workflows.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.isDefault ? `${workflow.name} (по умолчанию)` : workflow.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <p className={styles['hint']}>
          Ничего не выбрано — доска показывает все компоненты проекта, включая
          созданные позже.
        </p>
      </Modal>

      <Modal
        open={boardToScope !== null}
        onClose={() => setBoardToScope(null)}
        title={boardToScope ? `Компоненты доски «${boardToScope.name}»` : 'Компоненты доски'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setBoardToScope(null)} disabled={scopeMutation.isPending}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={scopeMutation.isPending}
              onClick={() => {
                if (boardToScope) scopeMutation.mutate(boardToScope);
              }}
            >
              Сохранить
            </Button>
          </>
        }
      >
        <div className={styles['form']}>
          <Combobox
            label="Компоненты"
            multiple
            items={componentItems}
            value={scopeComponentIds}
            isLoading={componentsQuery.isPending}
            ariaLabel="Компоненты доски"
            emptyText="В проекте пока нет компонентов"
            onChange={(value) => setScopeComponentIds(value)}
          />
          <Button
            size="sm"
            variant="ghost"
            icon={<Boxes size={14} />}
            disabled={scopeComponentIds.length === 0}
            onClick={() => setScopeComponentIds([])}
          >
            Показать все компоненты
          </Button>
          <p className={styles['hint']}>
            Задачи без выбранных компонентов на этой доске не показываются.
            Пустой выбор возвращает доску в состояние «все компоненты проекта».
            Архивные компоненты остаются в наборе, чтобы фильтр не терял задачи.
          </p>
        </div>
      </Modal>

      <Modal
        open={boardToRename !== null}
        onClose={() => setBoardToRename(null)}
        title="Переименовать доску"
        footer={
          <>
            <Button variant="ghost" onClick={() => setBoardToRename(null)} disabled={renameMutation.isPending}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={renameMutation.isPending}
              disabled={renameValue.trim().length === 0}
              onClick={() => {
                if (boardToRename) renameMutation.mutate(boardToRename);
              }}
            >
              Сохранить
            </Button>
          </>
        }
      >
        <Input
          label="Название доски"
          maxLength={80}
          value={renameValue}
          onChange={(event) => setRenameValue(event.target.value)}
        />
      </Modal>

      <ConfirmDialog
        open={boardToDelete !== null}
        title="Удалить доску?"
        description={
          boardToDelete
            ? `Доска «${boardToDelete.name}» исчезнет из меню. Задачи проекта останутся — доска их не хранит.`
            : ''
        }
        confirmLabel="Удалить"
        isPending={deleteMutation.isPending}
        onClose={() => setBoardToDelete(null)}
        onConfirm={() => {
          if (boardToDelete) deleteMutation.mutate(boardToDelete);
        }}
      />
    </div>
  );
}